import { EndPointDoc, Method } from '../apiref/apireftypes';
import { ApiRef } from '../fasades/apiref';
import { GenCodeCurl } from '../apiref/genCodeCurl';
import { GenCodeJavaScript } from '../apiref/genCodeJavaScript';
import { GenCodePython } from '../apiref/genCodePython';

export function newModel() : EndPointDoc
{
    const desc = <>
        <p>Create Model allows you to create a copy of an existing model (called the parent model). Once created, your new model is independent and can be customized through training. You can experiment with different training parameters, datasets, and fine-tuning techniques without any risk to the original parent model.</p>
        <p><strong>Note:</strong> You can only train models that you have created yourself. The standard base models (like Llama-3.1-8b or DeepSeek-R1-8b) are read-only, so you must first create a copy of these models before you can train them.</p>
    </>;

    const name = <>
        <p>The name for your new model. This will be used to identify the model in API calls and the model list.</p>
        <p><strong>Note:</strong> Model names must be unique within your account.</p>
    </>;

    const description = <>
        <p>A description of what this model is intended for or what makes it special. This helps you organize and identify your models.</p>
    </>;

    const parentModel = <>
        <p>The name of the parent model to base this new model on. This must be an existing model available in your model list.</p>
        <p>Common parent models include:</p>
        <ul>
            <li>Llama-3.1-8b - Meta's Llama 3.1 model</li>
            <li>DeepSeek-R1-8b - DeepSeek's R1 model</li>
            <li>Any model you have previously created</li>
        </ul>
    </>;

    const rank = <>
        <p>The rank parameter for LoRA (Low-Rank Adaptation) fine-tuning. This controls the dimensionality of the low-rank matrices used in the adaptation.</p>
        <ul>
            <li>Lower values (4-16) - Fewer parameters, faster training, less memory usage, but potentially less expressive</li>
            <li>Higher values (32-128) - More parameters, slower training, more memory usage, but potentially more expressive</li>
        </ul>
        <p><strong>Note:</strong> If not specified, the model will use a default rank value based on the parent model architecture.</p>
    </>;

    const alpha = <>
        <p>The alpha parameter for LoRA scaling. This controls the scaling factor applied to the LoRA weights.</p>
        <p>Typically set as a multiple of rank (e.g., if rank=16, alpha=32). A common rule of thumb is alpha = 2 * rank.</p>
        <p><strong>Note:</strong> If not specified, the model will use a default alpha value based on the rank.</p>
    </>;

    const dropout = <>
        <p>The dropout rate applied during training. Dropout helps prevent overfitting by randomly setting a fraction of input units to 0 during training.</p>
        <ul>
            <li>0.0 - No dropout (may lead to overfitting)</li>
            <li>0.1 - Light regularization (recommended for most cases)</li>
            <li>0.2-0.5 - Stronger regularization (use when overfitting is a concern)</li>
        </ul>
        <p><strong>Note:</strong> If not specified, the model will use a default dropout value.</p>
    </>;

    const learningRate = <>
        <p>The initial learning rate for training this model. This can be overridden when you actually train the model, but setting it here provides a default value.</p>
        <p>See the <a href="/apiref/train">Train endpoint</a> documentation for more details on learning rates.</p>
        <p><strong>Note:</strong> If not specified, the model will use a default learning rate based on the parent model.</p>
    </>;

    const responseType = ApiRef.getType("ModelInfo");

    const exampleUrl     = "v1/models";
    const exampleBody    = {
        "name"          : "MyCustomModel",
        "description"   : "A fine-tuned model for customer support tasks",
        "parent_model"   : "Llama-3.1-8b",
        "rank"          : 16,
        "alpha"         : 32,
        "dropout"       : 0.1,
        "learning_rate" : 1e-05
    };
    const genCurl        = new GenCodeCurl(Method.Post, exampleUrl, exampleBody);
    const genPython      = new GenCodePython(Method.Post, exampleUrl, exampleBody);
    const genJavaScript  = new GenCodeJavaScript(Method.Post, exampleUrl, exampleBody);
    const codeCurl       = genCurl.generate();
    let codePython       = genPython.generateImport();
    codePython          += genPython.generateRequest();
    codePython          += genPython.readResponse();
    let codeJavaScript   = genJavaScript.generateRequest();
    codeJavaScript      += genJavaScript.readResponse();

    return {
        "title"          : "Create Model",
        "method"         : Method.Post,
        "url"            : "v1/models",
        "docurl"         : "create-model",
        "description"    : desc,
        "seoDescription" : "Create a custom AI model by copying an existing base model. Set up LoRA fine-tuning parameters including rank, alpha, dropout, and learning rate for personalized model training.",
        "parameters"   : [
            {
                "name"        : "name",
                "type"        : "string",
                "description" : name,
                "optional"    : false
            },
            {
                "name"        : "description",
                "type"        : "string",
                "description" : description,
                "optional"    : false
            },
            {
                "name"        : "parent_model",
                "type"        : "string",
                "description" : parentModel,
                "optional"    : false
            },
            {
                "name"        : "rank",
                "type"        : "number",
                "description" : rank,
                "optional"    : true
            },
            {
                "name"        : "alpha",
                "type"        : "number",
                "description" : alpha,
                "optional"    : true
            },
            {
                "name"        : "dropout",
                "type"        : "number",
                "description" : dropout,
                "optional"    : true
            },
            {
                "name"        : "learning_rate",
                "type"        : "number",
                "description" : learningRate,
                "optional"    : true
            }
        ],
        "examples" : [
            {
                "url"            : exampleUrl,
                "responseType"   : responseType,
                "codeCurl"       : codeCurl,
                "codePython"     : codePython,
                "codeJavaScript" : codeJavaScript,
                "body"           : exampleBody,
                "output" : [{
                    "beta" : 0.8,
                    "default_model" : false,
                    "description" : "A fine-tuned model for customer support tasks",
                    "epsilon" : 0.2,
                    "id" : "mycustommodel",
                    "last_modified" : "2025-12-28T15:23:15.632Z",
                    "learning_rate" : 1e-05,
                    "learning_steps" : 5,
                    "name" : "MyCustomModel",
                    "parent_model" : "Llama-3.1-8b",
                    "read_only" : false,
                    "revision" : 0,
                    "temperature" : 0.7,
                    "top_p" : 0.95
                }]
            }
        ]
    };
}

